import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { GTM_CONTAINER_ID } from "@lib/utilities/analytics";

/**
 * Google Tag Manager, deferred to load+idle (D-perf, 2026-09-15).
 *
 * The container carries the Ads stack (~400KB with the AW tag) and the
 * official <GoogleTagManager> hardcodes afterInteractive — right in the
 * paint window. Two layers keep it out of the way:
 *
 *   1. the STATE GATE (the part that matters): the component mounts only
 *      after window load + requestIdleCallback, and mount-time is when it
 *      injects gtm.js — so the container never competes with first paint;
 *   2. next/dynamic (ssr: false): the wrapper chunk stays out of the main
 *      bundle. Cosmetic next to (1) — do not mistake it for the mechanism;
 *      dynamic WITHOUT the gate mounts at hydration and restores the
 *      contention.
 *
 * Consent is unaffected: the denied-by-default baseline is inlined by
 * _document ahead of every tag, so container tags wake up consent-gated.
 * Conversions fire later in-session; a container arriving at idle loses
 * nothing real.
 */
const GoogleTagManager = dynamic(
  () => import("@next/third-parties/google").then((m) => m.GoogleTagManager),
  { ssr: false }
);

export default function LazyGtm() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const arm = () => {
      const idle = (window as Window & {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
      }).requestIdleCallback;
      if (idle) idle(() => setReady(true), { timeout: 3000 });
      else setTimeout(() => setReady(true), 1500);
    };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
  }, []);

  if (!GTM_CONTAINER_ID || !ready) return null;
  return <GoogleTagManager gtmId={GTM_CONTAINER_ID} />;
}
